import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, BookOpen, Archive, Search, Newspaper, Landmark, ScrollText, Users, Map } from "lucide-react";
import { GLOBAL_COUNTRIES, COUNTRY_PROFILES, AUTH_SIGNUP_URL } from "@/lib/data";
import { NewsList } from "@/pages/Local";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const WING_ICONS = [Landmark, ScrollText, Users, Map];

export default function Region() {
  const { code } = useParams();
  const country = GLOBAL_COUNTRIES.find((c) => c.code === code);
  const profile = country ? COUNTRY_PROFILES[country.code] : null;
  const [bundle, setBundle] = useState(null);

  useEffect(() => {
    if (!country) return;
    setBundle(null);
    axios
      .get(`${API}/geo/country/${country.code}`, { params: { name: country.name } })
      .then(({ data }) => setBundle(data))
      .catch(() => setBundle({ article: null, news: [] }));
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!country) {
    return (
      <main className="max-w-[1600px] mx-auto px-6 md:px-10 pt-40 pb-32">
        <div className="overline">Not found</div>
        <h1 className="font-serif-display mt-2" style={{ fontSize: "clamp(48px, 8vw, 120px)" }}>
          This region isn't in the atlas yet.
        </h1>
        <Link to="/" className="btn-ghost mt-8 inline-flex" data-testid="region-back">
          <ArrowLeft size={16} /> Back to the index
        </Link>
      </main>
    );
  }

  const article = bundle?.article;
  const news = bundle?.news || [];

  return (
    <main data-testid={`region-page-${country.code}`} className="pt-32 md:pt-40">
      {/* Hero */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-16">
        <div className="flex items-center gap-3 text-[13px] mb-8">
          <Link to="/" className="link-underline text-[color:var(--ink-soft)]">Index</Link>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index">{country.region}</span>
          <span className="text-[color:var(--ink-soft)]">/</span>
          <span className="numeric-index uppercase">{country.code}</span>
        </div>
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 md:col-span-8">
            <div className="overline text-[color:var(--ink-soft)] mb-4">§ The {country.name} wing</div>
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, ease }}
              className="font-serif-display leading-[0.88] tracking-[-0.03em]"
              style={{ fontSize: "clamp(56px, 11vw, 200px)" }}
            >
              {country.name}
            </motion.h1>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="font-italic-serif italic text-lg leading-[1.6] text-[color:var(--ink-soft)]" data-testid="region-profile-note">
              {profile?.note || `Families of ${country.name} — their records, their villages, their journeys.`}
            </p>
          </div>
        </div>
      </section>

      {/* Records to search */}
      {profile && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20" data-testid="region-records">
          <div className="overline text-[color:var(--ink-soft)] mb-6 flex items-center gap-2">
            <Archive size={13} /> Where {country.name} keeps its families
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-l border-t border-[color:var(--ink-line)]">
            {profile.records.map((r, i) => {
              const Icon = WING_ICONS[i % WING_ICONS.length];
              return (
                <motion.div
                  key={r}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.04, ease }}
                  className="border-r border-b border-[color:var(--ink-line)] p-6 md:p-8 min-h-[160px] flex flex-col justify-between"
                  data-testid={`region-record-${i}`}
                >
                  <Icon size={18} className="text-[color:var(--sepia)]" />
                  <div className="font-serif-display mt-4" style={{ fontSize: "clamp(19px, 1.8vw, 26px)" }}>
                    {r}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* About the country — Wikipedia summary */}
      {article && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20" data-testid="region-article">
          <div className="rule-strong mb-10" />
          <div className="grid grid-cols-12 gap-8 items-start">
            {article.thumbnail && (
              <div className="col-span-12 md:col-span-3">
                <div className="vintage-frame">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover" style={{ filter: "sepia(20%)" }} />
                  </div>
                </div>
              </div>
            )}
            <div className={article.thumbnail ? "col-span-12 md:col-span-9" : "col-span-12"}>
              <div className="overline text-[color:var(--ink-soft)] mb-4 flex items-center gap-2">
                <BookOpen size={13} /> About {country.name}
              </div>
              <p className="text-[17px] leading-[1.75] max-w-[75ch]">{article.extract}</p>
              {article.url && (
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="region-wiki-link"
                  className="mt-4 inline-block text-sm link-underline text-[color:var(--rust)]"
                >
                  Continue reading on Wikipedia →
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Country headlines */}
      {news.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20" data-testid="region-news">
          <div className="flex items-center gap-4 mb-6">
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
            <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
              <Newspaper size={18} className="text-[color:var(--sepia)]" /> {country.name} in the news
            </h2>
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          </div>
          <NewsList news={news} testid="region-news-list" />
        </section>
      )}

      {/* CTAs */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-32">
        <div className="rule-strong mb-10" />
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-center">
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-serif-display leading-[0.98]" style={{ fontSize: "clamp(30px, 4vw, 60px)" }}>
              Roots in {country.name}?{" "}
              <span className="font-italic-serif italic text-[color:var(--ink-soft)]">
                Search the memorials, or begin the tree.
              </span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 flex md:flex-col gap-3 md:items-end">
            <Link to="/" className="btn-ghost" data-testid="region-search-cta">
              <Search size={14} /> Search memorials
            </Link>
            <a href={AUTH_SIGNUP_URL} className="btn-rust" data-testid="region-cta">
              Begin your archive <span aria-hidden>→</span>
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
